import { Params } from 'react-router-dom';
import { MAX_NETWORK_IDS } from '@/utils/constants';
import type { RouteParams, PageType } from '@/types';
import { isDateString } from './dateUtils/dateValidationUtils';
import { FilterRouteState } from './filterRouteStrategy';

/**
 * This object contains all the URL/path patterns for revenue dashboard.
 */
export const ROUTE_PATTERNS = {
  AUDIENCE: '/:networkId/audience/:showId?/:from?/:to?',
  CONTENT_PERFORMANCE: '/:networkId/content-performance/:showId?/:from?/:to?',
  EARNINGS: '/:networkId/earnings/:showId?/:from?/:to?',
  PAYOUTS: '/:networkId/payouts/:year?',
  SINGLE_PAYOUT: '/:networkId/payouts/:year/:month',
} as const;

/**
 * This class contains all the methods to build the URL/path for revenue dashboard.
 * @static earnings - builds the earnings path
 * @static contentPerformance - builds the content performance path
 * @static audience - builds the audience path
 * @static payouts - builds the payouts path
 * @static singlePayout - builds the single payout path
 */
export class RouteBuilder {
  static earnings(params: Pick<RouteParams, 'networkId' | 'showId' | 'from' | 'to'>): string {
    const { networkId, showId, from, to } = params;
    return `/${networkId}/earnings/${showId}${from && to ? `/${from}/${to}` : ''}`;
  }

  /**
   * Builds the multi-network content performance URL in query-param form
   * (`/content-performance?networks=A,B,C&shows=<showIds>&from=<from>&to=<to>`).
   *
   * `networkIds` is emitted as a comma-separated `networks` param. An empty `showIds` ("all
   * shows") maps to an omitted `shows` param; otherwise the ids are joined in the given order
   * (order is meaningful — it is not sorted like `networkIds`). Dates are only emitted when both
   * `from` and `to` are present.
   */
  static contentPerformance(
    params: Pick<RouteParams, 'from' | 'to'> & { networkIds: string[]; showIds: string[] }
  ): string {
    const { networkIds, showIds, from, to } = params;
    const searchParams = new URLSearchParams();

    const cleanedNetworkIds = networkIds
      .map((id) => id.trim())
      .filter(Boolean)
      .sort();
    if (cleanedNetworkIds.length > 0) searchParams.set('networks', cleanedNetworkIds.join(','));
    const cleanedShowIds = showIds.map((id) => id.trim()).filter(Boolean);

    if (cleanedShowIds.length > 0) {
      searchParams.set('shows', cleanedShowIds.join(','));
    }

    if (from && to) {
      searchParams.set('from', from);
      searchParams.set('to', to);
    }

    const search = searchParams.toString();
    return `/content-performance${search ? `?${search}` : ''}`;
  }

  /**
   * Builds the legacy path-based content performance URL
   * (`/:networkId/content-performance/:showId?/:from?/:to?`).
   *
   * `RouteBuilder.contentPerformance` emits the multi-network query-param form, so this is the
   * single source of truth for the legacy path shape.
   */
  static legacyContentPerformance(
    params: Pick<RouteParams, 'networkId' | 'showId' | 'from' | 'to'>
  ): string {
    const { networkId, showId, from, to } = params;
    return `/${networkId}/content-performance/${showId}${from && to ? `/${from}/${to}` : ''}`;
  }

  /**
   * Builds the content performance tab link for the sub-navigation.
   */
  static contentPerformanceTab(networkId: string): string {
    return RouteBuilder.contentPerformance({ networkIds: [networkId], showIds: [] });
  }

  static audience(params: Pick<RouteParams, 'networkId' | 'showId' | 'from' | 'to'>): string {
    const { networkId, showId, from, to } = params;
    return `/${networkId}/audience/${showId}${from && to ? `/${from}/${to}` : ''}`;
  }

  static payouts(params: Pick<RouteParams, 'networkId' | 'year'>): string {
    const { networkId, year } = params;
    return `/${networkId}/payouts${year ? `/${year}` : ''}`;
  }

  static singlePayout(params: Pick<RouteParams, 'networkId' | 'year' | 'month'>): string {
    const { networkId, year, month } = params;
    return `/${networkId}/payouts/${year}/${month}`;
  }

  /**
   * Gets the redirect path for a given page type.
   * Used to keep users on the same page type when switching networks.
   * Falls back to earnings if page type is not found.
   */
  static forPageType(
    pageType: PageType | undefined,
    networkId: string,
    from?: string,
    to?: string,
    year?: string
  ): string {
    const type = pageType || 'earnings';

    switch (type) {
      case 'audience':
        return RouteBuilder.audience({ networkId, showId: 'all', from, to });
      case 'content-performance':
        return RouteBuilder.contentPerformance({
          networkIds: [networkId],
          showIds: [],
          from,
          to,
        });
      case 'payouts':
        return RouteBuilder.payouts({ networkId, year: year || String(new Date().getFullYear()) });
      case 'earnings':
      default:
        return RouteBuilder.earnings({ networkId, showId: 'all', from, to });
    }
  }
}

/**
 * This helper function is used to get the current route params.
 * @param params - The params to get the current route params from.
 * @returns The current route params.
 */
export const getCurrentRouteParams = (params: Readonly<Params<string>>): RouteParams => ({
  networkId: params.networkId || '',
  showId: params.showId || 'all',
  from: params.from,
  to: params.to,
  year: params.year,
  month: params.month,
});

/**
 * Parses a comma-separated `networks` query value into a list of network ids — trimmed, with
 * blanks removed, preserving URL order. Returns `[]` for a null/undefined/empty param.
 * @param raw - The raw `networks` query param value.
 * @returns The parsed network ids.
 */
export const parseNetworkIdsParam = (raw: string | null | undefined): string[] =>
  raw
    ? raw
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean)
    : [];

/**
 * Parses a `?networks=` param into a deduped, capped list of network ids — trimmed, blanks
 * removed, URL order preserved. No authorization check; unknown ids are left in place and are
 * dropped naturally when callers map ids to network objects. Use {@link readSelectedNetworkIds}
 * when only authorized ids are needed (e.g. for UI chips where an unknown id would render broken).
 * @param raw - The raw `networks` query param value.
 * @param max - Maximum number of ids to return (defaults to {@link MAX_NETWORK_IDS}).
 * @returns The parsed, deduped, capped network ids in URL order.
 */
export const readNetworkIdsFromParam = (
  raw: string | null | undefined,
  max = MAX_NETWORK_IDS
): string[] => {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const id of parseNetworkIdsParam(raw)) {
    if (!seen.has(id)) {
      seen.add(id);
      result.push(id);
    }
  }
  return result.slice(0, max);
};

/**
 * Parses a comma-separated `shows` query value into a list of show ids — trimmed, with blanks
 * removed, preserving URL order. Returns `[]` for a null/undefined/empty param.
 * @param raw - The raw `shows` query param value.
 * @returns The parsed show ids.
 */
export const parseShowIdsParam = (raw: string | null | undefined): string[] =>
  raw
    ? raw
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean)
    : [];

/**
 * Parses a `?shows=` param into a deduped, capped list of show ids — trimmed, blanks removed,
 * URL order preserved.
 * @param raw - The raw `shows` query param value.
 * @param max - Maximum number of ids to return.
 * @returns The parsed, deduped, capped show ids in URL order.
 */
export const readShowIdsFromParam = (raw: string | null | undefined, max: number): string[] => {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const id of parseShowIdsParam(raw)) {
    if (!seen.has(id)) {
      seen.add(id);
      result.push(id);
    }
  }
  return result.slice(0, max);
};

/**
 * Filter/network state parsed from the multi-network content performance query string
 * (`?networks=A,B,C&shows=<showId>&from=<from>&to=<to>`).
 */
export interface ContentPerformanceQueryParams {
  /** Network ids from the comma-separated `networks` param, trimmed and de-blanked, in URL order. */
  networkIds: string[];
  /** Selected show ids from the comma-separated `shows` param, in URL order; an omitted/empty
   * param maps to `[]` ("all shows"). */
  showIds: string[];
  from: string | undefined;
  to: string | undefined;
}

/**
 * Reads the content performance filter state from query params, mirroring how
 * {@link getCurrentRouteParams} reads the legacy path params. Used by the query-mode content
 * performance route (`?networks=&shows=&from=&to=`).
 * @param searchParams - The current URL search params.
 * @returns The parsed network ids, show ids and date range.
 */
export const getContentPerformanceQueryParams = (
  searchParams: URLSearchParams
): FilterRouteState => {
  const networksParam = searchParams.get('networks');
  const showsParam = searchParams.get('shows');
  const from = searchParams.get('from') ?? undefined;
  const to = searchParams.get('to') ?? undefined;

  return {
    networkIds: parseNetworkIdsParam(networksParam),
    showIds: parseShowIdsParam(showsParam),
    from: isDateString(from) ? from : undefined,
    to: isDateString(to) ? to : undefined,
  };
};
